import { http, unwrap } from "@/lib/api/client";
import type {
  CategoryRequest,
  CreateTenantRequest,
  CreateUserRequest,
  LoginResponse,
  MenuCategoryDto,
  MenuItemDto,
  MenuItemRequest,
  MeResponse,
  OrderDto,
  OrderStatus,
  SalesSummaryRow,
  SalesToday,
  TenantDto,
  TopItemRow,
  UpdateTenantRequest,
  UpdateUserRequest,
  UserDto,
  ShopSummary,
} from "@/lib/api/types";

export const authApi = {
  login: (username: string, password: string) =>
    unwrap<LoginResponse>(http.post("/auth/login", { username, password })),
  me: () => unwrap<MeResponse>(http.get("/auth/me")),
};

export const tenantsApi = {
  list: () => unwrap<TenantDto[]>(http.get("/tenants")),
  get: (id: number) => unwrap<TenantDto>(http.get(`/tenants/${id}`)),
  create: (body: CreateTenantRequest) => unwrap<TenantDto>(http.post("/tenants", body)),
  update: (id: number, body: UpdateTenantRequest) => unwrap<TenantDto>(http.put(`/tenants/${id}`, body)),
};

export const usersApi = {
  /** Omit tenantId for all users platform-wide. */
  list: (tenantId?: number) => unwrap<UserDto[]>(http.get("/users", { params: tenantId != null ? { tenantId } : undefined })),
  create: (body: CreateUserRequest) => unwrap<UserDto>(http.post("/users", body)),
  update: (id: string, body: UpdateUserRequest) => unwrap<UserDto>(http.put(`/users/${id}`, body)),
};

/** SUPER_ADMIN scopes every menu call to a tenant via ?tenantId=. */
export const menuApi = {
  categories: (tenantId: number) =>
    unwrap<MenuCategoryDto[]>(http.get("/menu/categories", { params: { tenantId } })),
  createCategory: (tenantId: number, body: CategoryRequest) =>
    unwrap<MenuCategoryDto>(http.post("/menu/categories", body, { params: { tenantId } })),
  updateCategory: (tenantId: number, id: string, body: CategoryRequest) =>
    unwrap<MenuCategoryDto>(http.put(`/menu/categories/${id}`, body, { params: { tenantId } })),

  items: (tenantId: number) => unwrap<MenuItemDto[]>(http.get("/menu/items", { params: { tenantId } })),
  createItem: (tenantId: number, body: MenuItemRequest) =>
    unwrap<MenuItemDto>(http.post("/menu/items", body, { params: { tenantId } })),
  updateItem: (tenantId: number, id: string, body: MenuItemRequest) =>
    unwrap<MenuItemDto>(http.put(`/menu/items/${id}`, body, { params: { tenantId } })),
  uploadItemImage: (tenantId: number, id: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return unwrap<MenuItemDto>(http.post(`/menu/items/${id}/image`, form, { params: { tenantId } }));
  },
};

/** Admin portal views orders READ-ONLY — list/detail, no mutations. */
export const ordersApi = {
  list: (params: { tenantId: number; status?: OrderStatus; from?: string; to?: string }) =>
    unwrap<OrderDto[]>(http.get("/orders", { params })),
  get: (id: string) => unwrap<OrderDto>(http.get(`/orders/${id}`)),
};

/** Shop tenants (duka, hardware…): the shopkeeper's summary from menu-service's shop layer. */
export const shopApi = {
  summary: (tenantId: number) => unwrap<ShopSummary>(http.get("/inventory/shop-summary", { params: { tenantId } })),
};

export const salesApi = {
  today: (tenantId: number) => unwrap<SalesToday>(http.get("/sales/today", { params: { tenantId } })),
  summary: (tenantId: number, from: string, to: string) =>
    unwrap<SalesSummaryRow[]>(http.get("/sales/summary", { params: { tenantId, from, to } })),
  topItems: (tenantId: number, from: string, to: string) =>
    unwrap<TopItemRow[]>(http.get("/sales/top-items", { params: { tenantId, from, to } })),
};
